import {
  HYDRATE,
  type HydrateAction,
} from '@/domains/core/applicationState/store/hydrateAction';
import { HttpStatusCode } from '@/domains/core/httpClient/HttpStatusCode';
import { isB2C } from '@/domains/core/settings/utils';
import {
  FETCH_TOP_SPONSORED_PRODUCT_NO_DATA,
  FETCH_TOP_SPONSORED_PRODUCT_SUCCESS,
  Action as TopSponsoredProductBannerAction,
} from '@/productDiscovery/SponsoredProducts/saga/actions';

import type { Product } from '../algolia/interfaces/product';
import type { SpartacuxProductDiscoveryDomainState } from '../productDiscovery.model';
import {
  SEARCH_FAILED,
  SEARCH_FOR_FACET_VALUES_FAILED,
  SEARCH_FOR_FACET_VALUES_SUCCESS,
  SEARCH_SUCCESS,
  SEARCHING,
  SET_CATEGORIES_LIST,
  SET_CUSTOM_FACET,
  SET_FILTERS_CONFIG,
  SET_INSPECT_LISTINGS,
  SET_PAGE,
  SET_RERANKING_COUNT,
  SET_URL,
  type Action,
} from './actions';
import {
  initialState as listingInitialState,
  type ListingState,
} from './model';
import { selectListing } from './selectors/listing';

export const getListingReducer =
  <P extends Product = Product>() =>
  (
    state: ListingState = listingInitialState,
    action:
      | Action<P>
      | HydrateAction<SpartacuxProductDiscoveryDomainState>
      | TopSponsoredProductBannerAction,
  ): ListingState => {
    switch (action.type) {
      case HYDRATE: {
        const { products, facets, facetsStats, url, error, ...newState } =
          selectListing(action.state) ?? {};
        return {
          ...state,
          ...newState,
          ...(isB2C() ? { products, facets, facetsStats, url, error } : {}),
        };
      }
      case SEARCHING:
        return {
          ...state,
          fetchingAlgolia: true,
          products: [],
        };

      case SEARCH_SUCCESS:
        return {
          ...state,
          ...action.result,
          fetchingAlgolia: false,
        };

      case SEARCH_FAILED:
        return {
          ...state,
          error: {
            message: action.error.message,
            httpStatus: action.error.status ?? HttpStatusCode.INTERNAL_ERROR,
          },
          fetchingAlgolia: false,
        };

      case SET_PAGE:
        return {
          ...state,
          pageName: action.pageName,
        };

      case SET_URL:
        return {
          ...state,
          url: action.url,
        };

      case SET_INSPECT_LISTINGS:
        return {
          ...state,
          inspectListings: action.inspectListings,
        };

      case SET_FILTERS_CONFIG:
        return {
          ...state,
          filtersConfig: action.filtersConfig,
        };

      case SET_CUSTOM_FACET:
        return {
          ...state,
          customFacets: {
            ...state.customFacets,
            [action.facetName]: action.facet,
          },
        };

      case SEARCH_FOR_FACET_VALUES_SUCCESS:
        return {
          ...state,
          facets: {
            ...state.facets,
            [action.facetName]: action.facet,
          },
        };

      case SEARCH_FOR_FACET_VALUES_FAILED:
        return {
          ...state,
          error: {
            message: action.error.message,
            httpStatus: action.error.status ?? null,
          },
        };

      case SET_RERANKING_COUNT: {
        return {
          ...state,
          rerankingCount: action.rerankingCount,
          searchEngineProxy: action.searchEngineProxy ?? null,
          ruleContexts: action.ruleContexts,
        };
      }

      case SET_CATEGORIES_LIST: {
        return {
          ...state,
          categoriesList: action.categoriesList,
        };
      }

      case FETCH_TOP_SPONSORED_PRODUCT_SUCCESS: {
        return {
          ...state,
          topSponsoredProduct: {
            product: action.sponsoredProduct,
            fetched: true,
          },
        };
      }

      case FETCH_TOP_SPONSORED_PRODUCT_NO_DATA: {
        return {
          ...state,
          topSponsoredProduct: {
            product: undefined,
            fetched: true,
          },
        };
      }

      default:
        return state;
    }
  };
